'use client';
import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import { useForm } from 'react-hook-form';

import { EMAIL_REGEX } from 'lib/constants';
import { fetchJson, getFormData } from 'lib/fetch-json';
import useRecaptcha from 'src/hooks/useRecaptcha';

import Alert from 'src/components/Alert';
import Button from 'src/components/Button';
import FormError from 'src/components/FormError';

const AnimatedSmiley = dynamic(() => import('src/components/AnimatedSmiley'), {
    ssr: false,
});

export default function PageFormModal({ job }: { job: any }) {
    const { isReady, generateToken } = useRecaptcha();
    const [show, setShow] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(false);

    const {
        register,
        formState: { errors: _errors },
        handleSubmit,
    } = useForm({
        defaultValues: {
            name: '',
            email: '',
            phone: '',
            file: '',
            terms: false,
        },
    });
    const errors: any = _errors;

    const onSubmit = async (params: any) => {
        setErrorMessage(false);
        setLoading(true);
        try {
            const recaptcha = await generateToken();

            await fetchJson(`/api/forms/apply-job/${job.slug}`, {
                method: 'POST',
                body: getFormData({ ...params, file: params.file?.[0], recaptcha }),
            });

            setLoading(false);
            setShow(false);
            setShowSuccess(true);
        } catch (error: any) {
            setLoading(false);
            setErrorMessage(true);
        } finally {
            document.getElementById('job-application-container')?.scrollIntoView(true);
        }
    };

    return (
        <div id="job-application-container">
            {!showSuccess && (
                <div>
                    <h2 className="mb-4 text-xl md:text-2xl">Aplică online</h2>
                    {errorMessage && (
                        <Alert type="danger" className="mb-3">
                            A apărut o eroare la upload-ul documentului. Vă rugăm încercați mai târziu.
                        </Alert>
                    )}

                    <form
                        id="job-application-form"
                        className="flex flex-col gap-4"
                        noValidate
                        onSubmit={handleSubmit(onSubmit)}>
                        <div>
                            <input
                                type="text"
                                className={errors.name ? 'form-error' : ''}
                                id="name"
                                placeholder="Nume"
                                required
                                {...register('name', {
                                    required: 'Completarea numelui este obligatorie',
                                })}
                            />
                            <FormError error={errors.name} />
                        </div>

                        <div>
                            <input
                                type="email"
                                className={errors.email ? 'form-error' : ''}
                                id="email"
                                placeholder="Email"
                                required
                                {...register('email', {
                                    required: 'Completarea adresei de email este obligatorie',
                                    pattern: {
                                        value: EMAIL_REGEX,
                                        message: 'Adresa de email este incorectă',
                                    },
                                })}
                            />
                            <FormError error={errors.email} />
                        </div>

                        <div>
                            <input
                                type="tel"
                                className={errors.phone ? 'form-error' : ''}
                                id="phone"
                                placeholder="Telefon"
                                required
                                {...register('phone', {
                                    required: 'Completarea numărului de telefon este obligatorie',
                                })}
                            />
                            <FormError error={errors.phone} />
                        </div>

                        <div>
                            <input
                                type="file"
                                className={errors.file ? 'form-error' : ''}
                                id="file"
                                required
                                accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/x-iwork-pages-sffpages"
                                {...register('file', {
                                    required: 'Trebuie să selectați un fișier care conține CV-ul dvs.',
                                })}
                            />
                            <FormError error={errors.file} />
                        </div>

                        <div className="text-xs">
                            <p className="my-2">
                                În conformitate cu dispoziţiile <b>Regulamentului (UE) 679/2016</b> privind protecţia
                                persoanelor fizice în ceea ce priveşte prelucrarea datelor cu caracter personal şi
                                privind libera circulaţie a acestor date, sunt de acord cu prelucrarea datelor cu
                                caracter personal în vederea parcurgerii procedurii de angajare. Am fost informat că
                                aceste date vor fi prelucrate în bazele de date Bioclinica şi vor fi şterse sau distruse
                                la terminarea procesului de recrutare, în situaţia în care nu am fost admis.
                            </p>

                            <p className="my-2">
                                Am fost informat că datele personale vor fi transmise persoanei juridice care asigură
                                serviciile de medicina muncii în vederea supravegherii sănătăţii, în situaţia în care am
                                fost admis.
                            </p>

                            <p className="my-2">
                                Consimţământul poate fi retras în orice moment printr-o solicitare scrisă adresată
                                Responsabilului cu protecţia datelor cu caracter personal al Bioclinica, cu consecinţa
                                excluderii din procedură de angajare.
                            </p>
                        </div>

                        <div className="mb-3">
                            <label>
                                <input
                                    type="checkbox"
                                    {...register('terms', {
                                        required: 'Acceptarea termenilor și condițiilor este obligatorie.',
                                    })}
                                />{' '}
                                Am citit și sunt de acord cu termenii și condițiile legate de prelucrarea datelor
                            </label>
                            <FormError error={errors.terms} />
                        </div>
                    </form>
                    <div className="mt-4 flex justify-center gap-4">
                        <Button
                            type="submit"
                            className="w-full md:w-auto"
                            variant="primary"
                            form="job-application-form"
                            disabled={loading || !isReady}>
                            {job.slug === 'trimite-cv' ? 'Trimite CV' : 'Aplică pentru job'}
                        </Button>
                    </div>
                </div>
            )}
            {showSuccess && (
                <div className="text-center">
                    <div className="text-primary aspect-1 mx-auto w-3/4 md:w-1/2">
                        <AnimatedSmiley variant="happy" />
                    </div>
                    <h2 className="mb-4 text-3xl md:text-4xl">Succes!</h2>
                    <p className="mb-2">Mulțumim pentru completarea formularului!</p>
                    <p className="">Menţionăm că doar candidaţii consideraţi eligibili vor fi contactaţi.</p>
                </div>
            )}
        </div>
    );
}
